/**
 * Copyright © 2019 - Present, Vamstar Limited
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or
 * without modification, are not permitted.
 */
import React, { Component } from 'react';
import { IUser } from '@vamstar/fox-api-common/esm/user/types';
import { DEFAULT_PAGE_SIZE, XGridTable } from '@vamstar/fox-frontend-common';
import { compose } from 'recompose';
import { log } from '@vamstar/fox-node-logger';

import { columns } from './columns';
import { USERS } from './queries';
import { XGridTableWrapper } from '../../common/components/XGridTableWrapper';
import { WithCommonPagination, withCommonPagination } from '../../common/utils/WithCommonPagination';
import { XGridSearch } from '../../common/components/XGridSearch';

interface IUsersTableState {
  users: IUser[];
  isLoading: boolean;
  pageSize: number;
  total: number;
  currentPage: number;
  searchText: string;
}

type HOCProps = WithCommonPagination;

class UsersTableView extends Component<HOCProps, IUsersTableState> {
  state: IUsersTableState = {
    users: [],
    isLoading: false,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    currentPage: 1,
    searchText: '',
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    this.setState({ isLoading: true });
    const { commonPaginationService } = this.props;
    const { pageSize, currentPage, searchText } = this.state;
    try {
      const skip = (currentPage - 1) * pageSize;
      const usersResponse = await commonPaginationService.fetchPaginatedData<IUser>(USERS, skip, pageSize, searchText);
      if (usersResponse !== null) {
        const { items, total } = usersResponse.users;
        this.setState({ users: items, total, pageSize });
      }
    } catch (error) {
      log.error(JSON.stringify(error));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  onPageSizeChange = (newPageSize: number) => {
    this.setState({ pageSize: newPageSize, currentPage: 1 }, () => this.fetchData());
  };

  onPageChange = (page: number) => {
    this.setState({ currentPage: page }, () => this.fetchData());
  };

  public onSearchSubmit = () => {
    this.setState({ currentPage: 1 }, () => this.fetchData());
  };

  onSearchChange = (searchText: string) => {
    this.setState({ searchText });
  };

  render() {
    const { isLoading, users, pageSize, total, currentPage, searchText } = this.state;
    return (
      <XGridTableWrapper heading="Users" route="/user" buttonLabel="Add User">
        <XGridSearch
          value={searchText}
          onSearchSubmit={() => this.fetchData()}
          onSearchChange={(val) => this.onSearchChange(val)}
        />
        <XGridTable
          columns={columns}
          rows={users}
          count={total}
          loading={isLoading}
          onPageChange={this.onPageChange}
          onPageSizeChange={this.onPageSizeChange}
          page={currentPage}
          rowsPerPage={pageSize}
        />
      </XGridTableWrapper>
    );
  }
}

const UsersTable: React.ComponentClass<any, IUsersTableState> = compose<HOCProps, any>(withCommonPagination)(
  UsersTableView,
);

export default UsersTable;
